import { Box, Container, SimpleGrid, Stack, Text } from '@mantine/core';

import { MarketingEyebrow } from '@/components/atoms';
import { useInView } from '@/hooks/useInView';
import { T } from '@/theme/assay';

type UseCase = {
  /** Who you are when this lands. */
  who: string;
  /** What Assay does about it. One sentence, no hedging. */
  what: string;
};

// Straight from the Fazier launch (docs/launch/) - the section that made people
// say "that's me". Order matters: paid work first, then the life events.
const USE_CASES: UseCase[] = [
  {
    who: 'Consultants and freelancers',
    what: 'Keep past clients warm so repeat work arrives before the pipeline runs dry.',
  },
  {
    who: 'Founders',
    what: 'Stay in front of the investors, advisors and operators you will need, six months before you need them.',
  },
  {
    who: 'Anyone job-hunting',
    what: 'Reactivate a network before you have to ask it for something.',
  },
  {
    who: 'After a job change',
    what: 'The 500 people you worked with are now the 500 people you are about to lose touch with.',
  },
  {
    who: 'Post-conference follow-up',
    what: 'Turn a stack of badges into people you actually speak to again.',
  },
  {
    who: 'Just being a better friend',
    what: 'The people who got you here, remembered on purpose rather than with a pang of guilt.',
  },
];

function UseCaseCard({
  useCase,
  order,
  inView,
}: {
  useCase: UseCase;
  order: number;
  inView: boolean;
}) {
  const base = 0.55 + order * 0.07;

  return (
    <Box
      component="li"
      style={{
        position: 'relative',
        paddingTop: 20,
        borderTop: `1px solid ${T.border}`,
        opacity: 0,
        animation: inView
          ? `assay-word-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${base}s both`
          : undefined,
      }}
    >
      {/* Ore seam that draws across the rule as the card arrives */}
      <Box
        aria-hidden
        style={{
          position: 'absolute',
          top: -1,
          left: 0,
          width: 40,
          height: 1,
          background: T.oreLight,
          boxShadow: `0 0 8px ${T.oreLight}`,
          transformOrigin: 'left',
          animation: inView
            ? `assay-eyebrow-rule-draw 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${base + 0.15}s both`
            : undefined,
        }}
      />
      <Text
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontWeight: 500,
          fontSize: '1.25rem',
          lineHeight: 1.25,
          color: T.textPrimary,
          marginBottom: 8,
        }}
      >
        {useCase.who}
      </Text>
      <Text
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: '0.78rem',
          lineHeight: 1.7,
          color: T.textMuted,
        }}
      >
        {useCase.what}
      </Text>
    </Box>
  );
}

export function UseCasesSection() {
  const { ref, inView } = useInView(0.1);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="use-cases-heading"
      id="use-cases"
      style={{ position: 'relative', overflow: 'hidden', scrollMarginTop: 96 }}
    >
      <Container size="lg">
        <Stack gap={40}>
          <Stack gap={16} maw={680}>
            <MarketingEyebrow label="Use cases" inView={inView} />

            <Box
              component="h2"
              id="use-cases-heading"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 300,
                fontSize: 'clamp(1.9rem, 3.4vw, 2.75rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: T.textPrimary,
                margin: 0,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.45s both' : undefined,
              }}
            >
              Who this is for.
            </Box>

            <Text
              size="sm"
              c="dimmed"
              style={{
                maxWidth: 620,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.58s both' : undefined,
              }}
            >
              Anyone whose next opportunity comes from someone they already know. If one of these is
              you, Assay was built for the exact week you are having.
            </Text>
          </Stack>

          <SimpleGrid
            component="ul"
            cols={{ base: 1, sm: 2, md: 3 }}
            spacing="xl"
            verticalSpacing={40}
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {USE_CASES.map((useCase, i) => (
              <UseCaseCard key={useCase.who} useCase={useCase} order={i} inView={inView} />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}
